'use client';

import { useFiltersContext } from '@components/FilterPage/Context/useFiltersContext';
import { GridLayoutItem } from '@components/Layouts';
import { Box, Button, Flex, freddyTheme, Switch, Typography } from '@coolblue-development/freddy';
import openFilterOverlayTrigger from '@coolblue-development/ga4-events/triggers/RecruitmentFilter/openFilterOverlayTrigger';
import showFilterResultsTrigger from '@coolblue-development/ga4-events/triggers/RecruitmentFilterResults/showFilterResultsTrigger';
import { CheckmarkCircled, Sliders, SmileHappy } from '@coolblue-development/icons';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useTranslation } from '@i18n/useTranslation';
import { filterCategories, SearchResults } from '@server/careers-backend/search';
import { useSearchParams } from 'next/navigation';
import { useDeferredValue, useEffect, useMemo, useState } from 'react';

import { ResultCounter } from '../ResultCounter';
import { FilterFacetsContent } from './FilterFacetsContent';
import { FilterFacetsModal } from './FilterFacetsModal';
import { SelectedFilters } from './SelectedFilters';

type Props = {
  searchResults: SearchResults;
  hiddenFilters?: string[];
};

export const FilterFacets = ({ searchResults, hiddenFilters }: Props) => {
  const deferredValue = useDeferredValue(searchResults);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { trackEvent } = useAnalytics();
  const searchParams = useSearchParams();
  const { t } = useTranslation('filterPage.filterFacets');

  const hasSelectedFilters = Array.from(searchParams.keys()).some(
    key => key !== 'visibility' && key in filterCategories
  );

  const { getSelectedFilterOptions, setFilter, isInternalUser } = useFiltersContext();

  const internalOnly = getSelectedFilterOptions('visibility').includes('internal');

  useEffect(() => {
    if (searchParams.size) {
      trackEvent(
        showFilterResultsTrigger({
          option_names: searchResults.foundPages.map(foundVacancy => foundVacancy.id).join(',')
        })
      );
    }
  }, [searchResults, trackEvent, searchParams]);

  const visibleFilters = useMemo(() => {
    const hiddenFilterKeys = new Set(['visibility', ...(hiddenFilters ?? [])]);

    return Object.fromEntries(
      Object.entries(deferredValue.filters ?? {}).filter(([key]) => !hiddenFilterKeys.has(key))
    ) as typeof deferredValue.filters;
  }, [deferredValue, hiddenFilters]);

  return (
    <>
      {/* Desktop */}
      <GridLayoutItem
        hideUntil='md'
        column='1'
        paddingTop={false}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: freddyTheme.spacing[400]
        }}
      >
        <Typography variant='h2' mb={0}>
          {t('title')}
        </Typography>
        {isInternalUser && (
          <Flex alignItems='center' gap={freddyTheme.spacing[200]}>
            <Switch
              checked={internalOnly}
              onChange={() => setFilter('visibility', internalOnly ? [] : ['internal'])}
              sx={{
                backgroundColor: internalOnly
                  ? freddyTheme.color.background.interactiveActionBase
                  : undefined
              }}
              aria-label='Internal vacancies switcher'
            />
            <Flex alignItems='center' gap={freddyTheme.spacing[100]}>
              <SmileHappy size='large' color={freddyTheme.color.font.headingMain} />
              <Typography
                fontWeight={freddyTheme.typography.fontWeight.dobraSlab.medium}
                fontFamily='DobraSlabMedium'
                color={freddyTheme.color.font.headingMain}
              >
                {t('visibility.label')}
              </Typography>
            </Flex>
          </Flex>
        )}
        {hasSelectedFilters && <SelectedFilters filters={visibleFilters} />}
        <FilterFacetsContent version='full' filters={visibleFilters} />
      </GridLayoutItem>

      {/* Mobile */}
      <GridLayoutItem
        hideFrom='md'
        row='1'
        column='1 / -1'
        paddingTop={false}
        padding={{ x: 400 }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: freddyTheme.spacing[200]
        }}
      >
        <ResultCounter count={deferredValue.pagination.total} />
        <Box>
          <Button
            startIcon={<Sliders />}
            endIcon={
              hasSelectedFilters && (
                <CheckmarkCircled color={freddyTheme.color.icon.sentimentPositive} />
              )
            }
            variant='secondary'
            onClick={() => {
              setIsModalOpen(true);
              trackEvent(openFilterOverlayTrigger({ sub_feature: 'top' }));
            }}
          >
            {t('title')}
          </Button>
        </Box>
      </GridLayoutItem>

      <Box
        hideFrom='md'
        sx={{
          position: 'fixed',
          bottom: '1.2rem',
          left: 0,
          zIndex: 1,
          width: '100vw',
          display: 'flex',
          justifyContent: 'center'
        }}
      >
        <Button
          hideFrom='md'
          startIcon={<Sliders />}
          endIcon={
            hasSelectedFilters && (
              <CheckmarkCircled
                color={freddyTheme.color.icon.sentimentPositive}
                innerFill='contrast'
              />
            )
          }
          onClick={() => {
            setIsModalOpen(true);
            trackEvent(openFilterOverlayTrigger({ sub_feature: 'bottom' }));
          }}
          sx={{
            width: '20rem'
          }}
        >
          {t('title')}
        </Button>
      </Box>

      <FilterFacetsModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        filters={visibleFilters}
        resultCount={searchResults.pagination.total}
      />
    </>
  );
};
